import { useState } from 'react';
import { isSafeUrl } from '../utils/url';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from './ui/dropdown-menu';
import {
  Bell,
  Check,
  CheckCheck,
  X,
  Clock,
  AlertCircle,
  UserPlus,
  ShieldCheck,
  CreditCard,
  Trash2,
  RefreshCw
} from 'lucide-react';
import { useNotificationContext } from '../contexts/NotificationContext';
import { Notification } from '../api/notifications';
import { formatDistanceToNow } from 'date-fns';
import { getNotificationColor, getNotificationIcon } from '@/helpers/common';

interface NotificationDropdownProps {
  className?: string;
}

const NotificationItem: React.FC<{
  notification: Notification;
  onMarkAsRead: (id: number) => void;
  onDelete: (id: number) => void;
}> = ({ notification, onMarkAsRead, onDelete }) => {
  const [isActioning, setIsActioning] = useState(false);

  const handleMarkAsRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!notification.read) {
      setIsActioning(true);
      await onMarkAsRead(notification.id);
      setIsActioning(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsActioning(true);
    await onDelete(notification.id);
    setIsActioning(false);
  };

  const handleNotificationClick = async () => {
    if (!notification.read) {
      await onMarkAsRead(notification.id);
    }
    if (notification.url && isSafeUrl(notification.url)) {
      window.location.href = notification.url;
    }
  };

  return (
    <div
      className={`p-3 border-b border-border hover:bg-accent cursor-pointer transition-colors ${!notification.read ? 'bg-accent/50 border-l-4 border-l-primary' : ''
        }`}
      onClick={handleNotificationClick}
    >
      <div className="flex items-start space-x-3">
        <div className={`mt-0.5 ${getNotificationColor(notification.type)}`}>
          {getNotificationIcon(notification.type)}
        </div>

        <div className="flex-1 min-w-0">
          <p className={`text-sm ${!notification.read ? 'font-semibold text-foreground' : 'text-foreground'}`}>
            {notification.message}
          </p>

          {notification.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {notification.description}
            </p>
          )}

          <div className="flex items-center space-x-2 mt-2">
            <Clock className="w-3 h-3 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(notification.createdon), { addSuffix: true })}
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
          {!notification.read && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 hover:bg-primary/10"
              onClick={handleMarkAsRead}
              disabled={isActioning}
              title="Mark as read"
            >
              <Check className="w-3 h-3" />
            </Button>
          )}

          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
            onClick={handleDelete}
            disabled={isActioning}
            title="Delete notification"
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ className }) => {
  const {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications
  } = useNotificationContext();

  const [isOpen, setIsOpen] = useState(false);

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleRefresh = async () => {
    await refreshNotifications();
  };

  const recentNotifications = notifications.slice(0, 10);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={`relative bg-background hover:bg-accent rounded-lg shadow-sm ${className}`}
        >
          <Bell className="w-4 h-4" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-80 bg-popover shadow-lg rounded-lg border border-border p-0">
    
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <DropdownMenuLabel className="text-lg font-semibold text-foreground p-0">
              Notifications
            </DropdownMenuLabel>

            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={loading}
                className="h-8 w-8 p-0"
                title="Refresh notifications"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>

              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                  className="h-8 px-3 text-xs"
                  title="Mark all as read"
                >
                  <CheckCheck className="w-4 h-4 mr-1" />
                  Mark all read
                </Button>
              )}
            </div>
          </div>

          {unreadCount > 0 && (
            <p className="text-sm text-muted-foreground mt-1">
              {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </p>
          )}
        </div>

        {error && (
          <div className="p-4 bg-destructive/10 border-l-4 border-destructive">
            <div className="flex items-center">
              <AlertCircle className="w-4 h-4 text-destructive mr-2" />
              <p className="text-sm text-destructive-foreground">{error}</p>
            </div>
          </div>
        )}

        {loading && notifications.length === 0 && (
          <div className="p-8 text-center">
            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Loading notifications...</p>
          </div>
        )}

        {!loading && notifications.length === 0 && !error && (
          <div className="p-8 text-center">
            <Bell className="w-8 h-8 text-muted-foreground/50 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground font-medium">No notifications</p>
          </div>
        )}

        {recentNotifications.length > 0 && (
          <ScrollArea className="h-96">
            <div className="divide-y divide-border">
              {recentNotifications.map((notification) => (

                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={markAsRead}
                  onDelete={deleteNotification}
                />
              ))}
            </div>
          </ScrollArea>
        )}

   
        {notifications.length > 0 && (
          <DropdownMenuSeparator />
        )}
        {notifications.length > recentNotifications.length && (
          <DropdownMenuItem
            className="text-center py-3 text-sm font-medium text-primary hover:text-primary/80 hover:bg-accent"
            onSelect={(e) => {
              e.preventDefault();
              window.location.href = '/notifications';
            }}
          >
            View all notifications ({notifications.length})
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
